import { useState, useEffect, useRef } from 'react'
import L from 'leaflet'
import { Navigation, Timer, X, Loader2, MapPin } from 'lucide-react'
import { supabase } from '../lib/supabase'

// Fix leaflet default marker icon broken in Vite
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

const DURATIONS = [
  { label: '30 min', value: 30 },
  { label: '1 hr',  value: 60 },
  { label: '2 hr',  value: 120 },
  { label: '3 hr',  value: 180 },
  { label: 'Open',  value: null },
]

function formatElapsed(startedAt) {
  const secs = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatRemaining(startedAt, durationMins) {
  if (!durationMins) return null
  const remainMs = new Date(startedAt).getTime() + durationMins * 60 * 1000 - Date.now()
  if (remainMs <= 0) return 'Expired'
  const m = Math.floor(remainMs / 60000)
  const s = Math.floor((remainMs % 60000) / 1000)
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m left`
  return `${m}m ${s}s left`
}

// ── Leaflet map rendered directly via useEffect (no react-leaflet) ────────────
function LeafletMap({ lat, lng, label }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 17,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map)
    L.marker([lat, lng]).addTo(map).bindPopup(label)
    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [lat, lng, label])

  return <div ref={containerRef} style={{ height: '160px', width: '100%' }} />
}

export default function ParkingSession({ tagCode, userId, plate }) {
  const [session, setSession]           = useState(null)
  const [loadingSession, setLoadingSession] = useState(true)
  const [showPicker, setShowPicker]     = useState(false)
  const [duration, setDuration]         = useState(60)
  const [starting, setStarting]         = useState(false)
  const [ending, setEnding]             = useState(false)
  const [tick, setTick]                 = useState(0)
  const [error, setError]               = useState('')
  const timerRef = useRef(null)

  // Load active session + subscribe to real-time changes
  useEffect(() => {
    if (!tagCode) return

    // Initial fetch — get most recent active session
    supabase
      .from('parking_sessions')
      .select('*')
      .eq('tag_code', tagCode)
      .eq('active', true)
      .order('started_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        setSession(data?.[0] || null)
        setLoadingSession(false)
      })

    // Real-time subscription — syncs across all devices
    const channel = supabase
      .channel(`parking_${tagCode}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'parking_sessions',
        filter: `tag_code=eq.${tagCode}`,
      }, ({ eventType, new: newRow }) => {
        if (eventType === 'INSERT' && newRow?.active) setSession(newRow)
        if (eventType === 'UPDATE' && newRow?.tag_code === tagCode) {
          setSession(newRow.active === true ? newRow : null)
        }
        if (eventType === 'DELETE') setSession(null)
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [tagCode])

  // Live timer tick every second
  useEffect(() => {
    if (!session) return
    timerRef.current = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(timerRef.current)
  }, [session])

  // Notify owner when timer expires (browser notification + WhatsApp via edge function)
  useEffect(() => {
    if (!session?.duration_minutes) return
    const msLeft = new Date(session.started_at).getTime() + session.duration_minutes * 60 * 1000 - Date.now()
    if (msLeft <= 0) return

    const id = setTimeout(async () => {
      // Browser notification
      if (Notification.permission === 'granted') {
        new Notification('ParkPing — Parking time up!', {
          body: `Your parking session for ${plate} has expired.`,
          icon: '/favicon.ico',
        })
      }
      // WhatsApp alert via edge function
      await supabase.functions.invoke('notify-parking-expiry', {
        body: { sessionId: session.id },
      })
    }, msLeft)

    return () => clearTimeout(id)
  }, [session, plate])

  async function startSession() {
    setError('')
    setStarting(true)
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 })
      )
      // Deactivate any existing active sessions for this tag first
      await supabase
        .from('parking_sessions')
        .update({ active: false, ended_at: new Date().toISOString() })
        .eq('tag_code', tagCode)
        .eq('active', true)

      const { data, error: err } = await supabase
        .from('parking_sessions')
        .insert({
          tag_code: tagCode,
          user_id: userId,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          duration_minutes: duration || null,
          active: true,
        })
        .select()
        .single()
      if (err) throw err

      if (Notification.permission === 'default') Notification.requestPermission()
      setSession(data)
      setShowPicker(false)
    } catch (e) {
      console.error('Parking session error:', e)
      if (e.code === 1) setError('Location access denied. Please allow location in your browser.')
      else setError(e?.message || 'Failed to start session. Try again.')
    } finally {
      setStarting(false)
    }
  }

  async function endSession() {
    setEnding(true)
    await supabase
      .from('parking_sessions')
      .update({ active: false, ended_at: new Date().toISOString() })
      .eq('id', session.id)
    setSession(null)
    setEnding(false)
  }

  if (loadingSession) return null

  // ── Active session ────────────────────────────────────────────────────────
  if (session) {
    const elapsed   = formatElapsed(session.started_at)
    const remaining = formatRemaining(session.started_at, session.duration_minutes)
    const isExpired = remaining === 'Expired'
    const accentColor = isExpired ? '#ef4444' : '#00e5a0'

    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: `1px solid ${isExpired ? 'rgba(239,68,68,0.4)' : 'rgba(0,229,160,0.2)'}`, background: '#13131a', marginTop: '8px' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5"
          style={{ background: isExpired ? 'rgba(239,68,68,0.08)' : 'rgba(0,229,160,0.07)' }}
        >
          <div className="flex items-center gap-2">
            <MapPin size={13} style={{ color: accentColor }} />
            <span className="text-xs font-bold" style={{ color: accentColor }}>
              {isExpired ? 'Parking Expired' : 'Parking Active'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Timer size={11} className="text-brand-muted" />
              <span className="text-xs font-mono font-bold text-brand-text">{elapsed}</span>
              {remaining && <span className="text-xs" style={{ color: accentColor }}>· {remaining}</span>}
            </div>
            <button onClick={endSession} disabled={ending} className="text-brand-muted hover:text-red-400 transition-colors">
              {ending ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
            </button>
          </div>
        </div>

        {/* Map */}
        <LeafletMap lat={session.lat} lng={session.lng} label={`${plate} parked here`} />

        {/* Navigate button */}
        <button
          onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${session.lat},${session.lng}`, '_blank')}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-all hover:opacity-90"
          style={{ background: isExpired ? 'rgba(239,68,68,0.1)' : 'rgba(0,229,160,0.1)', color: accentColor }}
        >
          <Navigation size={12} />
          Navigate to My Car
        </button>
      </div>
    )
  }

  // ── Start button / picker ─────────────────────────────────────────────────
  return (
    <div style={{ marginTop: '8px' }}>
      {!showPicker ? (
        <button
          onClick={() => setShowPicker(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-xs font-bold transition-all hover:opacity-90"
          style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.07)', color: '#7a798a' }}
        >
          <MapPin size={12} />
          Start Parking Session
        </button>
      ) : (
        <div className="rounded-2xl p-4 space-y-3" style={{ background: '#1c1c26', border: '1px solid rgba(0,229,160,0.2)' }}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-brand-text">How long are you parking?</p>
            <button onClick={() => setShowPicker(false)} className="text-brand-muted hover:text-brand-text">
              <X size={14} />
            </button>
          </div>

          <div className="flex gap-2 flex-wrap">
            {DURATIONS.map((d) => (
              <button
                key={d.label}
                onClick={() => setDuration(d.value)}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  background: duration === d.value ? 'rgba(0,229,160,0.15)' : '#13131a',
                  border: `1px solid ${duration === d.value ? '#00e5a0' : 'rgba(255,255,255,0.07)'}`,
                  color: duration === d.value ? '#00e5a0' : '#7a798a',
                }}
              >
                {d.label}
              </button>
            ))}
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <button
            onClick={startSession}
            disabled={starting}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold text-bg transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#00e5a0' }}
          >
            {starting ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
            {starting ? 'Getting location…' : 'Pin My Location & Start'}
          </button>
        </div>
      )}
    </div>
  )
}
